import React, { useEffect, useLayoutEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AiOutlineUser } from 'react-icons/ai';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import {
  authenticationService,
  connectionService,
  invitationService,
  profileService,
  communityService,
  eventService,
} from '../../services';
import {
  CustomFieldResponses,
  CustomFieldType,
  DashboardMemberFieldType,
  ExternalMemberInviteData,
  InviteType,
} from '../../types/invites';
import { ProfileDataType, ProfileField } from '../../types/profile';
import {
  addPhoneNumberCountryCode,
  createEmptyCustomFieldResponses,
  createEmptyField,
  formatCustomFieldResponsesFromCustomFields,
} from '../../utils/formatUtils';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import TermsAndConditions from '../../components/misc/TermsAndConditions';
import ProfileFieldForm from '../../components/forms/profile/ProfileFieldForm';
import EmailInput from '../../components/forms/profile/EmailInput';
import CommunicationNameInput from '../../components/forms/profile/CommunicationNameInput';
import NameInput from '../../components/forms/profile/NameInput';
import { sortProfileFields } from '../../utils/sortUtils';
import { checkMandatoryFields, isEmptyField, isValidProfileFields } from '../../utils/validationUtils';
import { profileDataTypeIcons } from '../../utils/iconUtils';
import { profileFieldToString } from '../../utils/stringUtils';
import { LoginPageState, SuccessPageState } from '../../types/navigation';
import Button from '../../components/misc/Button';
import { UserAccountCreationChannel, UserAccountCreationEvent } from '../../analytics/models';
import AccountExistsModal from '../../components/modals/AccountExistsModal';
import useMagicLogin from '../../hooks/useMagicLogin';
import useDeleteParams from '../../hooks/useDeleteParams';
import { CommunityOverview } from '../../components/misc/CommunityOverview';
import AttendanceComponent from '../../components/misc/AttendanceComponent';
import { EventParticipationAnswer, PersonalEvent } from '../../types/event';

export default function UnauthenticatedLocalContactInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const invitationId = params.get('invitationId') || params.get('token');
  const link = params.get('link');
  const eventId = params.get('eventId');

  if (useMagicLogin()) {
    return <></>;
  }

  useDeleteParams('link');

  const [invite, setInvite] = useState<ExternalMemberInviteData>();
  const [alias, setAlias] = useState<string>('');
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>(
    createEmptyCustomFieldResponses([]),
  );
  const [validate, setValidate] = useState<boolean>(false);

  const [accountExists, setAccountExists] = useState<boolean>(false);
  const [accountExistsModal, setAccountExistsModal] = useState<boolean>(false);
  const [terms, setTerms] = useState<boolean>(false);
  const [event, setEvent] = useState<PersonalEvent>();
  const [eventAnswer, setEventAnswer] = useState<EventParticipationAnswer | null>(null);
  const [eventAnswered, setEventAnswered] = useState(false);

  const emailField = profileFields.find((x) => x.dataType === ProfileDataType.EMAIL);

  useEffect(() => {
    if (!invitationId) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
    }
  }, []);

  useLayoutEffect(() => {
    if (!invitationId || link) return;

    const setFields = async () => {
      const inv =
        (location.state?.networkInvite as ExternalMemberInviteData) ||
        (await invitationService.getExternalMemberInvite(invitationId).catch(() => {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        }));
      if (!inv) return;
      setInvite(inv);

      const {
        mandatoryFields,
        optionalFields,
        customFields,
        receivedData,
        alias: aliasInvite,
        customFieldResponses: cfr,
      } = inv;
      const dataRequest = Array.from(new Set([...mandatoryFields, ...optionalFields, DashboardMemberFieldType.EMAIL]));
      setProfileFields(
        sortProfileFields(
          dataRequest
            .map(
              (f) => receivedData?.find((x) => x.dataType === f || x.social?.socialType === f) || createEmptyField(f),
            )
            .filter((x) => !!x) as ProfileField[],
        ),
      );
      setAlias(aliasInvite ?? '');
      setCustomFieldResponses(formatCustomFieldResponsesFromCustomFields(customFields, cfr ?? {}));
    };

    setFields();

    if (eventId) {
      eventService
        .getEventWithToken(invitationId, 'external-invite')
        .then((evt) => {
          setEvent(evt);
        })
        .catch(() => {
          toast.error(t('toast.error.general.eventNotFound'));
        });
    }
  }, []);

  const validateFields = (): boolean => {
    if (!alias || !alias.split('+')[0] || !alias.split('+')[1]) {
      toast.error(t('toast.error.field.name'));
      return false;
    }
    if (!terms) {
      toast.error(t('toast.error.general.terms'));
      return false;
    }
    if (isEmptyField(profileFields.find((x) => x.dataType === ProfileDataType.EMAIL)!)) {
      toast.error(t('toast.error.field.email'));
      return false;
    }
    return isValidProfileFields(profileFields);
  };

  const handleSave = async () => {
    if (!invite) return;
    setValidate(true);
    if (!validateFields()) return;
    if (!checkMandatoryFields(invite!, profileFields, customFieldResponses)) return;
    if (accountExists) {
      setAccountExistsModal(true);
      return;
    }

    if (eventId && !eventAnswered) {
      toast.error(t('toast.error.general.pleaseAnswerEvent'));
      return;
    }

    const creationEvent: UserAccountCreationEvent = {
      channel: UserAccountCreationChannel[UserAccountCreationChannel.LOCAL_CONTACT_INVITE],
      details: undefined,
    };
    const login = await authenticationService.register(alias, emailField?.email!, creationEvent);

    const properties = await profileService.addFields(
      profileFields
        .filter((x) => !isEmptyField(x) && x.dataType !== ProfileDataType.EMAIL)
        .map(addPhoneNumberCountryCode),
    );

    await connectionService.acceptExternalMemberInvite(invite.dashboardId, invitationId!, {
      sharedPropertiesWithMembers: [],
      customFieldResponses,
      sharedPropertiesWithDashboard: properties
        .filter(
          (f) => (f.id && f.dataType !== ProfileDataType.EMAIL) || !invite.nonRequestedFields.includes(f.dataType),
        )
        .map((x) => +x.id!),
    });

    if (eventId && eventAnswer) {
      await eventService.respondToEvent(eventId, eventAnswer);
    }

    const state: SuccessPageState = {
      translationKey: `page.common.success.message.${
        invite?.communityId ? 'businessCommunityInvite' : 'businessInvite'
      }`,
      translationOptions: {
        businessName: invite?.name,
      },
      networkInvite: invite,
    };

    if (eventId && invite?.communityId) {
      navigate('/community/events', { state: { communityId: invite.communityId } });
    } else if (invite.communityId && invite.communityFeatures?.EASY_SHARING) {
      navigate(`/communities/${invite.communityId}/members`, { state: { invite, success: state, replace: true } });
    } else {
      navigate('/success', { state });
    }

    setTimeout(async () => {
      login();
      try {
        await communityService.fetchCommunities();
      } catch (error) {
        // ignore - will refresh on next load
      }
    }, 1);
  };

  const handleEventAnswerChange = (answer: EventParticipationAnswer) => {
    setEventAnswer(answer);
    setEventAnswered(true);
  };

  const handleLogin = async () => {
    const state: LoginPageState = {
      email: emailField?.email! || '',
      invitationId: invitationId!,
      inviteType: InviteType.LOCAL_CONTACT,
      networkInvite: invite,
    };
    navigate('/login', { state });
  };

  const setProfileFieldByType = (field: ProfileField) => {
    if (field.dataType === ProfileDataType.SOCIAL) {
      setProfileFields(
        profileFields.map(
          (f: ProfileField): ProfileField => (f.social?.socialType === field.social?.socialType ? field : f),
        ),
      );
    } else
      setProfileFields(
        profileFields.map((f: ProfileField): ProfileField => (f.dataType === field.dataType ? field : f)),
      );
  };

  const requesterName = invite?.name || '';
  const communicationName = invite?.customFields.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);

  const handleMagicGeneration = async () => {
    await authenticationService.generateMagicLink(
      invite?.receivedData?.find((f) => f.dataType === ProfileDataType.EMAIL)?.email!,
      invitationId!,
      undefined,
      InviteType.LOCAL_CONTACT,
    );
    toast.success(t('toast.success.login'));
  };

  if (invite?.accountExists)
    return (
      <ResponsePageWrapper handleLogin={handleLogin}>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}
        <div className="flex flex-col">
          <h2 className="text-lg font-medium">{t('page.localContactInvite.noAuthHasAccount.title')}</h2>
          <p className="text-sm">{t('page.localContactInvite.noAuthHasAccount.message')}</p>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <div className="flex gap-2">
            <div className="flex h-fit items-center justify-center rounded-[6px] bg-secondary p-1 text-secondary-50">
              <AiOutlineUser className="h-5 w-5" strokeWidth="3" />
            </div>
            <p className="my-auto text-sm">{invite?.alias?.replace('+', ' ') || ''}</p>
          </div>
          {invite?.receivedData
            ?.filter((f) => !isEmptyField(f))
            .map((f) => (
              <div className="flex gap-2" key={f.dataType}>
                <div className="flex h-fit items-center justify-center rounded-[6px] bg-secondary p-1 text-secondary-50">
                  {profileDataTypeIcons[f.dataType]}
                </div>
                <p className="my-auto text-sm">{profileFieldToString(f)}</p>
              </div>
            ))}
        </div>

        <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button className="underline" onClick={() => navigate('/')}>
            {t('general.cancel')}
          </Button>
          <Button variant="primary" className="px-[32px]" onClick={handleMagicGeneration}>
            {t('page.localContactInvite.noAuthHasAccount.button')}
          </Button>
        </div>
      </ResponsePageWrapper>
    );

  return (
    <>
      <ResponsePageWrapper handleLogin={handleLogin}>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}
        <div className="flex flex-col">
          <h2 className="mb-4 text-2xl font-semibold">{t('page.shared.shareData')}</h2>
          <p className="text-sm">{t('page.localContactInvite.noAuthNoAccount.message', { name: requesterName })}</p>
        </div>
        {eventId && event && (
          <div className="bg-secondary-10 mt-4 rounded-lg border border-secondary-50 p-4">
            <h2 className="mb-2 text-lg font-medium">{t('page.events.infoScreen.attendance.title')}</h2>
            <p className="mb-2 text-lg">{event.title}</p>
            <p className="text-md mb-4">{t('page.events.infoScreen.attendance.question')}</p>
            <AttendanceComponent selectedAnswer={eventAnswer} setSelectedAnswer={handleEventAnswerChange} />
          </div>
        )}
        <div className="mt-4 flex flex-col gap-4">
          <NameInput alias={alias} setAlias={setAlias} mandatory validate={validate} />

          {communicationName && (
            <CommunicationNameInput
              name={customFieldResponses[communicationName.customFieldId]}
              validate={validate}
              setName={(name) =>
                setCustomFieldResponses({
                  ...customFieldResponses,
                  [communicationName.customFieldId]: name,
                })
              }
              mandatory={communicationName?.mandatory}
            />
          )}

          {emailField && (
            <EmailInput
              field={emailField!}
              emailCheck
              validate={validate}
              mandatory
              setAccountExists={setAccountExists}
              setField={setProfileFieldByType}
            />
          )}
          {accountExists ? (
            <span className="text-sm text-error">
              {t('page.shared.emailInUse')}{' '}
              <strong className="cursor-pointer underline" onClick={handleLogin}>
                {t('page.shared.logInHere')}
              </strong>
              .
            </span>
          ) : (
            ''
          )}

          {profileFields.map((field) => {
            return field.dataType === ProfileDataType.EMAIL ? (
              <></>
            ) : (
              <ProfileFieldForm
                field={field}
                canBeEmpty={!invite?.mandatoryFields.includes(field.dataType)}
                validate={validate}
                mandatory={invite?.mandatoryFields.includes(field.dataType)}
                setField={setProfileFieldByType}
              />
            );
          })}
          <CustomFieldsForm
            fields={invite?.customFields || []}
            response={customFieldResponses}
            setResponse={setCustomFieldResponses}
          />
        </div>
        <div className="mt-4">
          <TermsAndConditions checked={terms} setChecked={setTerms} />
        </div>
        <div className="flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button variant="primary" className="px-[32px]" onClick={handleSave}>
            {t('page.shared.acceptInvite')}
          </Button>
        </div>
      </ResponsePageWrapper>
      {emailField?.email && invitationId && (
        <AccountExistsModal
          open={accountExistsModal}
          setOpen={setAccountExistsModal}
          request={{
            inviteType: InviteType.LOCAL_CONTACT,
            invitationId,
            email: emailField.email,
          }}
        />
      )}
    </>
  );
}
